import { type Registry as Services, service } from '@ember/service';
import BaseRepositoryService from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type {
  DisputeEvidenceFileField,
  DisputeEvidenceTextField,
} from 'uplisting-frontend/models/dispute';

export type DisputeEvidenceDraft = Partial<
  Record<DisputeEvidenceTextField, string>
>;

export const DISPUTE_EVIDENCE_MAX_FILE_BYTES = 4_500_000;

export const DISPUTE_EVIDENCE_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
];

export default class DisputeRepositoryService extends BaseRepositoryService<
  ModelRegistry['dispute']
> {
  @service httpClient!: Services['http-client'];
  @service fileHandler!: Services['file-handler'];

  recordName = 'dispute';
  implementMethods = ['findAll', 'findRecord', 'query'];

  public async saveEvidence(
    dispute: ModelRegistry['dispute'],
    evidence: DisputeEvidenceDraft,
  ): Promise<void> {
    await this.#patch(dispute, { evidence });
  }

  public async submitEvidence(
    dispute: ModelRegistry['dispute'],
    evidence: DisputeEvidenceDraft = {},
  ): Promise<void> {
    await this.#patch(dispute, { evidence, submit: true });
  }

  public async accept(dispute: ModelRegistry['dispute']): Promise<void> {
    await this.#patch(dispute, { accept: true });
  }

  public async refund(dispute: ModelRegistry['dispute']): Promise<void> {
    await this.#patch(dispute, { refund: true });
  }

  public async attachFile(
    dispute: ModelRegistry['dispute'],
    field: DisputeEvidenceFileField,
    file: File,
  ): Promise<void> {
    const body = new window.FormData();

    body.append('dispute_id', dispute.id);
    body.append('field', field);
    body.append('file', file, file.name);

    const instance = await this.httpClient.getInstance();

    await instance.post('/v2/dispute_evidence_files', body);

    await this.#reload(dispute);
  }

  public async prefill(
    dispute: ModelRegistry['dispute'],
  ): Promise<DisputeEvidenceDraft> {
    const instance = await this.httpClient.getInstance();

    const { data } = await instance.post('/v2/dispute_evidence_prefills', {
      dispute_id: Number(dispute.id),
    });

    await this.#reload(dispute);

    return (data?.meta?.evidence_suggestions ?? {}) as DisputeEvidenceDraft;
  }

  public async detachFile(
    dispute: ModelRegistry['dispute'],
    field: DisputeEvidenceFileField,
  ): Promise<void> {
    const instance = await this.httpClient.getInstance();

    await instance.delete(`/v2/dispute_evidence_files/${field}`, {
      params: { dispute_id: dispute.id },
    });

    await this.#reload(dispute);
  }

  public async downloadFile(
    dispute: ModelRegistry['dispute'],
    field: DisputeEvidenceFileField,
  ): Promise<void> {
    await this.fileHandler.getFile(`/v2/dispute_evidence_files/${field}`, {
      dispute_id: dispute.id,
    });
  }

  async #patch(
    dispute: ModelRegistry['dispute'],
    attributes: object,
  ): Promise<void> {
    const instance = await this.httpClient.getInstance();

    await instance.patch(`/v2/disputes/${dispute.id}`, {
      data: { id: Number(dispute.id), attributes },
    });

    await this.#reload(dispute);
  }

  async #reload(dispute: ModelRegistry['dispute']): Promise<void> {
    await this.findRecord(dispute.id, { reload: true });
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/dispute': DisputeRepositoryService;
  }
}
